import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils/utils";
import Text from "@/components/ui/text";
import Input from "@/components/ui/input";
import IconContainer from "@/components/ui/icon-container";
import { Bullet, Rhombus } from "@dreamhub/designsystem/icons";
import {
  useThresholdById,
  useUpdateThresholdHigh
} from "@/entities/settings/hooks";

interface SimpleGuardrailsProps {
  questionCount?: number;
}

const SimpleGuardrails: React.FC<SimpleGuardrailsProps> = ({
  questionCount = 0
}) => {
  const { t } = useTranslation();

  const { data: thresholdData, isLoading: isLoadingThreshold } =
    useThresholdById("qualification_score");

  const updateThresholdMutation = useUpdateThresholdHigh("qualification_score");
  const [percentage, setPercentage] = useState("");

  useEffect(() => {
    if (thresholdData?.high !== undefined) {
      setPercentage(thresholdData.high.toString());
    }
  }, [thresholdData?.high]);

  const handlePercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty string or numbers from 0-100
    if (
      value === "" ||
      (/^\d+$/.test(value) && parseInt(value) >= 0 && parseInt(value) <= 100)
    ) {
      setPercentage(value);
    }
  };

  const handlePercentageBlur = () => {
    if (percentage !== "" && thresholdData) {
      const newHighValue = parseInt(percentage);
      if (newHighValue !== thresholdData.high) {
        updateThresholdMutation.mutate({
          data: { high: newHighValue }
        });
      }
    }
  };

  const calculatedFieldCount = useMemo(() => {
    if (questionCount === 0) return 0;
    const percentageNum = parseInt(percentage) || 0;
    return Math.ceil((questionCount * percentageNum) / 100);
  }, [questionCount, percentage]);

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex items-center gap-3">
        <IconContainer size="5" color="secondary">
          <Rhombus />
        </IconContainer>
        <Text variant="body">
          {t("settings.deals.workflow.guardrails.allRequiredFieldsYes")}
        </Text>
      </div>

      <div className="flex items-center gap-3">
        <IconContainer size="5" color="positive">
          <Bullet />
        </IconContainer>
        <div className="flex justify-between items-center flex-1">
          <Text variant="body">
            {t("settings.deals.workflow.guardrails.percentageYesAnswers")}
          </Text>
          <div className="flex flex-col items-start gap-1">
            <Input
              type="text"
              value={percentage}
              onChange={handlePercentageChange}
              onBlur={handlePercentageBlur}
              disabled={isLoadingThreshold || updateThresholdMutation.isPending}
              className={cn(
                "w-28 h-8 px-2 py-1 text-base",
                "border border-border-border2 rounded-md",
                "focus:border-primary focus:outline-none",
                (isLoadingThreshold || updateThresholdMutation.isPending) &&
                  "opacity-50 cursor-not-allowed"
              )}
              maxLength={3}
              trailingIcon={<div className="ml-[-20px]">%</div>}
            />
            <div className="">
              <Text variant="smBodySecondary">
                {t("settings.deals.workflow.guardrails.aboveFieldsCount", {
                  count: calculatedFieldCount
                })}
              </Text>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleGuardrails;
